import { TasksType, UnitName } from '@prisma/client';
import { QueryParamsType } from 'types';
import { z } from 'zod';
import ApiError from './error';
import {
  buildClassroomErrorMessages,
  optionalGradesSchema,
  repeatedGradesQuerySchema,
  requireClassroomGrade,
  requiredGradesSchema,
  selectableGradeSchema,
} from './validation/classroomGradeSchemas';
import { buildNotebookErrorMessages } from './validation/notebookTaskSchemaMessages';
export { orgAdminClassroomListQuerySchema } from './validation/orgAdminClassroomListSchema';
/**
 * Validation split ownership:
 * - classroom/task lifecycle schemas live here
 * - assignment/organization/product-key schemas live in validationAssignmentOrganizationSchemas.ts
 * This keeps the current split explicit without changing import paths.
 */
export const verifyUserSchema = z.object({
  userId: z.string().trim().cuid({ message: 'Invalid user id' }),
  token: z.string().trim().uuid({ message: 'Invalid token' }),
});
export const userIdCuidSchema = z.object({
  id: z.string().cuid(),
});
export const validateUserIdCuid = (id: string): string => {
  const result = userIdCuidSchema.safeParse({ id });
  if (!result.success) throw ApiError.fromZodError(result.error);
  return result.data.id;
};

export const validateVerifyUserParams = (
  params: QueryParamsType
): { userId: string; token: string } => {
  const result = verifyUserSchema.safeParse(params);
  if (!result.success) throw ApiError.fromZodError(result.error);
  return result.data;
};

const classroomNameMin = 3,
  classroomNameMax = 30,
  classroomDescriptionMin = 3,
  classroomDescriptionMax = 250,
  classroomDate = z.coerce.date(),
  classroomNicknameMin = 3,
  classroomNicknameMax = 30;

const classroomErrorMessages = buildClassroomErrorMessages({
  nameMin: classroomNameMin,
  nameMax: classroomNameMax,
  descriptionMin: classroomDescriptionMin,
  descriptionMax: classroomDescriptionMax,
  nicknameMin: classroomNicknameMin,
  nicknameMax: classroomNicknameMax,
});

/** 
* @swagger
components:
  schemas:
    ClassroomCreate:
      type: object
      properties:
        displayName:
          type: string
          minLength: ${classroomNameMin}
          maxLength: ${classroomNameMax}
        description:
          type: string
          minLength: ${classroomDescriptionMin}
          maxLength: ${classroomDescriptionMax}
        grade:
          type: string
          enum:
            - ${classroomGrade}
          deprecated: true
        grades:
          type: array
          items:
            type: string
            enum:
              - ${classroomGrade}
        startDate:
          type: string
          format: date
        endDate:
          type: string
          format: date
        nickname:
          type: string
          minLength: ${classroomNameMin}
          maxLength: ${classroomNameMax}
          nullable: true
      required:
        - displayName
        - description
        - grades
        - startDate
        - endDate
      example:
        displayName: "Classroom 1"
        description: "This is a sample description for classroom 1"
        grades: ["FIRST"]
        startDate: "2023-02-08"
        endDate: "2023-03-08"
        nickname: "Class 1"
        */
const classroomCreateBaseSchema = z.object({
  displayName: z
    .string()
    .min(classroomNameMin, { message: classroomErrorMessages.nameMinError })
    .max(classroomNameMax, { message: classroomErrorMessages.nameMaxError }),
  description: z
    .string()
    .min(classroomDescriptionMin, {
      message: classroomErrorMessages.descriptionMinError,
    })
    .max(classroomDescriptionMax, {
      message: classroomErrorMessages.descriptionMaxError,
    }),
  grade: selectableGradeSchema.optional(),
  grades: requiredGradesSchema,
  startDate: classroomDate,
  endDate: classroomDate,
  nickname: z
    .string()

    .min(classroomNameMin, {
      message: classroomErrorMessages.nicknameMinError,
    })
    .max(classroomNameMax, {
      message: classroomErrorMessages.nicknameMaxError,
    })
    .optional(),
});

export const classroomCreateSchema = requireClassroomGrade(
  classroomCreateBaseSchema
).refine((data) => data.startDate < data.endDate, {
  message: 'Start date must be before end date',
  path: ['startDate'],
});

const orgTeacherIdSchema = z
  .string()
  .trim()
  .cuid({ message: 'Invalid teacher id' });

const orgAdminClassroomCreateBaseSchema = classroomCreateBaseSchema.extend({
  teacherIds: z
    .array(orgTeacherIdSchema)
    .min(1, { message: 'Select at least one teacher' }),
});

export const orgAdminClassroomCreateSchema = requireClassroomGrade(
  orgAdminClassroomCreateBaseSchema
).refine((data) => data.startDate < data.endDate, {
  message: 'Start date must be before end date',
  path: ['startDate'],
});

export const classroomUpdateSchema = z.object({
  displayName: z
    .string()
    .min(classroomNameMin, { message: classroomErrorMessages.nameMinError })
    .max(classroomNameMax, { message: classroomErrorMessages.nameMaxError })
    .optional(),
  nickname: z.string().optional(),
  description: z
    .string()
    .min(classroomDescriptionMin)
    .max(classroomDescriptionMax)
    .optional(),
  grade: selectableGradeSchema.optional(),
  grades: optionalGradesSchema,
  endDate: classroomDate.optional(),
});

export const classroomDeleteSchema = z.object({
  id: z.string().cuid(),
});

export const classroomGetSchema = z.object({
  id: z.string().cuid(),
});

export const classroomGetAllSchema = z.object({
  userId: z.string().cuid(),
});

export const classroomIdSchema = z.object({
  classroomId: z.string().cuid(),
});

export const organizationIdSchema = z.object({
  organizationId: z.string().cuid(),
});

export const removeStudentSchema = z.object({
  classroomId: z.string().cuid(),
  userId: z.string().cuid(),
});

export const assignmentIdSchema = z.object({
  assignmentId: z.string().cuid(),
});

export const accessCodeForClassroomSchema = z
  .object({
    accessCode: z
      .string()
      .trim()
      .min(6, {
        message: 'Access code must be at least 6 characters long',
      })
      .max(6, {
        message: 'Access code must be at most 6 characters long',
      }),
  })
  .refine(
    (data) => {
      // access code must be alphanumeric
      const alphanumeric = /^[a-z0-9]+$/i;
      if (!alphanumeric.test(data.accessCode)) {
        return false;
      }
      return true;
    },
    {
      message: 'Access code must be alphanumeric (letters and numbers)',
      path: ['accessCode'],
    }
  );

export const classroomIdWithAccessCodeSchema = z
  .object({
    classroomId: z.string().trim().cuid(),
    accessCode: z.string().trim().min(6).max(6),
  })
  .refine(
    (data) => {
      // access code must be alphanumeric
      const alphanumeric = /^[a-z0-9]+$/i;
      if (!alphanumeric.test(data.accessCode)) {
        return false;
      }
      return true;
    },
    {
      message: 'Access code must be alphanumeric (letters and numbers)',
      path: ['accessCode'],
    }
  );

// ----------- manual validation with safeParse() -------------

export const validateClassroomId = (classroomId: string): string => {
  const result = classroomIdSchema.safeParse({ classroomId });
  if (!result.success) throw ApiError.fromZodError(result.error);

  return result.data.classroomId;
};

export const validateOrganizationId = (organizationId: string): string => {
  const result = organizationIdSchema.safeParse({ organizationId });
  if (!result.success) throw ApiError.fromZodError(result.error);

  return result.data.organizationId;
};

// --------------- notebooks ---------------

const notebookNameMin = 2,
  notebookNameMax = 30,
  notebookDescriptionMin = 3,
  notebookDescriptionMax = 990,
  notebookSummaryMin = 3,
  notebookSummaryMax = 990;

const notebookErrorMessages = buildNotebookErrorMessages({
  nameMin: notebookNameMin,
  nameMax: notebookNameMax,
  descriptionMin: notebookDescriptionMin,
  descriptionMax: notebookDescriptionMax,
  summaryMin: notebookSummaryMin,
  summaryMax: notebookSummaryMax,
});

const notebookCreateSchemaBase = z.object({
  displayName: z
    .string()
    .min(notebookNameMin, {
      message: notebookErrorMessages.nameMinError,
    })
    .max(notebookNameMax, {
      message: notebookErrorMessages.nameMaxError,
    }),
  notebookDescription: z
    .string()
    .min(notebookDescriptionMin, {
      message: notebookErrorMessages.notebookDescriptionMinError,
    })
    .max(notebookDescriptionMax, {
      message: notebookErrorMessages.notebookDescriptionMaxError,
    }),
  notebookSummary: z.preprocess(
    (value) =>
      typeof value === 'string' && value.trim().length === 0
        ? undefined
        : value,
    z
      .string()
      .trim()
      .min(notebookSummaryMin, {
        message: notebookErrorMessages.notebookSummaryMinError,
      })
      .max(notebookSummaryMax, {
        message: notebookErrorMessages.notebookSummaryMaxError,
      })
      .optional()
  ),
  unit: z.nativeEnum(UnitName, {
    message: notebookErrorMessages.unitError,
  }),
  grade: selectableGradeSchema.optional(),
  grades: optionalGradesSchema,
  challenges: z.array(z.string()).optional(),
  type: z.nativeEnum(TasksType).optional(),
  textLabelSetId: z.string().cuid({
    message: notebookErrorMessages.notebookTemplateError,
  }),
  rubricEnabled: z.boolean().optional(),
  rubricVersion: z.string().trim().min(1).max(120).nullable().optional(),
});

const notebookDraftCreateSchemaBase = notebookCreateSchemaBase.partial({
  displayName: true,
  grade: true,
  notebookDescription: true,
  notebookSummary: true,
  challenges: true,
  textLabelSetId: true,
  unit: true,
});

export const notebookCreateSchema = notebookDraftCreateSchemaBase.extend({
  scope: z.enum(['USER', 'SYSTEM', 'PARTNER']).optional(),
});

export const organizationNotebookCreateSchema =
  notebookDraftCreateSchemaBase.extend({
    scope: z.never().optional(),
  });

export const notebookUpdateSchema = z.object({
  displayName: z
    .string()
    .min(notebookNameMin, {
      message: notebookErrorMessages.nameMinError,
    })
    .max(notebookNameMax, {
      message: notebookErrorMessages.nameMaxError,
    })
    .optional(),
  notebookDescription: z
    .string()
    .min(notebookDescriptionMin, {
      message: notebookErrorMessages.notebookDescriptionMinError,
    })
    .max(notebookDescriptionMax, {
      message: notebookErrorMessages.notebookDescriptionMaxError,
    })
    .optional(),
  notebookSummary: z.preprocess(
    (value) =>
      typeof value === 'string' && value.trim().length === 0
        ? undefined
        : value,
    z
      .string()
      .trim()
      .min(notebookSummaryMin, {
        message: notebookErrorMessages.notebookSummaryMinError,
      })
      .max(notebookSummaryMax, {
        message: notebookErrorMessages.notebookSummaryMaxError,
      })
      .optional()
  ),
  unit: z.nativeEnum(UnitName).optional(),
  grade: selectableGradeSchema.optional(),
  grades: optionalGradesSchema,
  challenges: z.array(z.string()).optional(),
  textLabelSetId: z.string().cuid().optional(),
  rubricEnabled: z.boolean().optional(),
  rubricVersion: z.string().trim().min(1).max(120).nullable().optional(),
});

export const notebookGetSchema = z.object({
  id: z.string().cuid(),
});

export const optionalSourceFrameworkIdSchema = z.preprocess(
  (value) =>
    typeof value === 'string' && value.trim().length === 0 ? undefined : value,
  z.string().trim().cuid().optional()
);

export const notebookFrameworkSnapshotBodySchema = z.preprocess(
  (value) => {
    if (value == null) {
      return {};
    }

    if (typeof value === 'string' && value.trim().length === 0) {
      return {};
    }

    return value;
  },
  z.object({
    sourceFrameworkId: optionalSourceFrameworkIdSchema,
  })
);

export const notebookLessonPlanUpdateSchema = z.discriminatedUnion(
  'sourceType',
  [
    z.object({
      sourceType: z.literal('TEXT'),
      text: z.string().trim().min(1, {
        message: 'Lesson plan text is required.',
      }),
    }),
    z.object({
      sourceType: z.literal('FILE'),
      fileKey: z.string().trim().min(1, {
        message: 'Lesson plan file key is required.',
      }),
      fileType: z.string().trim().min(1, {
        message: 'Lesson plan file type is required.',
      }),
      fileName: z.string().trim().optional(),
      fileBucket: z.string().trim().optional(),
      fileSize: z.number().int().positive().optional(),
    }),
  ]
);

export const notebookQueryParams = z.object({
  assignmentType: z.nativeEnum(TasksType).optional(),
  isAssignmentCreationStep: z.string().optional(),
  grade: selectableGradeSchema.optional(),
  grades: repeatedGradesQuerySchema,
  unit: z.nativeEnum(UnitName).optional(),
  collectionId: z.string().cuid().optional(),
  search: z.string().trim().min(1).optional(),
  ownership: z.enum(['ALL', 'PERSONAL', 'SHARED']).optional(),
  frameworkState: z.enum(['ALL', 'AVAILABLE', 'HIDDEN']).optional(),
  sortBy: z.enum(['NAME_ASC', 'NEWEST', 'OLDEST']).optional(),
  includeArchived: z.enum(['true', 'false']).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  cursor: z.string().cuid().optional(),
});

// Backward-compatible aliases while routes/types finish migrating.
export const taskCreateSchema = notebookCreateSchemaBase.extend({
  scope: z.enum(['USER', 'SYSTEM']).optional(),
});
export const taskUpdateSchema = notebookUpdateSchema;
export const taskGetSchema = notebookGetSchema;
export const taskQueryParams = notebookQueryParams;

// --------------- groupSets ---------------
